import { useMemo, useState } from 'react'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import { getErrorMessage } from '@/lib/api'
import eventTagsApi from '@/services/event-tags'
import type { EventTag } from '@/types'

type EventTagsSelectProps = {
  value: string[]
  onValueChange: (value: string[]) => void
  tags: EventTag[]
  disabled?: boolean
  id?: string
  ariaLabel?: string
  placeholder?: string
  allowCreate?: boolean
}

export function EventTagsSelect({
  value,
  onValueChange,
  tags,
  disabled = false,
  id,
  ariaLabel = 'Event tags',
  placeholder = 'Select tags',
  allowCreate = true,
}: EventTagsSelectProps) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [newTagTitle, setNewTagTitle] = useState('')

  const selectedTags = useMemo(
    () => tags.filter((tag) => value.includes(String(tag.id))),
    [tags, value],
  )

  const filteredTags = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return tags
    }
    return tags.filter((tag) => `${tag.title} ${tag.code}`.toLowerCase().includes(query))
  }, [search, tags])

  const createTagMutation = useMutation({
    mutationFn: (title: string) => eventTagsApi.create({ title }),
    onSuccess: (createdTag) => {
      queryClient.invalidateQueries({ queryKey: ['event-tags'] })
      onValueChange(Array.from(new Set([...value, String(createdTag.id)])))
      setNewTagTitle('')
      toast.success('Tag created')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const toggleTag = (tagId: string) => {
    if (value.includes(tagId)) {
      onValueChange(value.filter((entry) => entry !== tagId))
      return
    }
    onValueChange([...value, tagId])
  }

  const triggerLabel = selectedTags.length > 0
    ? selectedTags.map((tag) => tag.title).join(', ')
    : placeholder

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', !selectedTags.length && 'text-muted-foreground')}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b p-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tags..."
          />
        </div>
        <ScrollArea className="max-h-56">
          {filteredTags.length === 0 ? (
            <p className="px-3 py-4 text-sm text-muted-foreground">No tags found.</p>
          ) : (
            <div className="p-1">
              {filteredTags.map((tag) => {
                const tagId = String(tag.id)
                const selected = value.includes(tagId)
                return (
                  <button
                    key={tagId}
                    type="button"
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent',
                      selected && 'bg-accent',
                    )}
                    onClick={() => toggleTag(tagId)}
                  >
                    <Check className={cn('h-4 w-4', selected ? 'opacity-100' : 'opacity-0')} />
                    <span className="truncate">{tag.title}</span>
                  </button>
                )
              })}
            </div>
          )}
        </ScrollArea>
        {allowCreate ? (
          <div className="flex items-center gap-2 border-t p-2">
            <Input
              value={newTagTitle}
              onChange={(event) => setNewTagTitle(event.target.value)}
              placeholder="New tag name"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  const title = newTagTitle.trim()
                  if (title) {
                    createTagMutation.mutate(title)
                  }
                }
              }}
            />
            <Button
              type="button"
              size="icon"
              variant="outline"
              disabled={!newTagTitle.trim() || createTagMutation.isPending}
              onClick={() => {
                const title = newTagTitle.trim()
                if (title) {
                  createTagMutation.mutate(title)
                }
              }}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
